import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRightLeft,
  BookCheck,
  ChevronRight,
  CircleDashed,
  Filter,
  Send,
  ShieldCheck,
  Sparkles,
  Split,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  DataTable,
  arrIncludesSome,
  type FacetedFilter,
} from '@/components/ui/DataTable';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { formatCurrency, formatDate } from '@/lib/formatters';
import {
  usePostTransactionsToQbo,
  useChartOfAccounts,
  useQbEntities,
  useSuggestedQbCategoriesBatch,
  useTransactions,
  useTransactionSplitsSummary,
  useUpdateTransactionEntity,
  useUpdateTransactionQbAccount,
  type QbEntityPick,
  type TransactionListRow,
} from '@/features/financials/hooks';
import {
  useBulkPostToQbo,
  type BulkPostRowState,
} from '@/features/financials/useBulkPostToQbo';
import {
  isWithinDrilldownRange,
  useTxDrilldownFilter,
} from '@/features/financials/transactionsDrilldown';
import {
  buildCoaHierarchyByType,
  findLockedAccountIds,
  formatQbAccountLabel,
  QB_GROUP_ORDER,
} from '@/features/financials/coaHierarchy';
import { ExpenseStatusEditor } from '@/features/expenses/ExpenseStatus';
import {
  bankFailLabel,
  isBankFailStatus,
} from '@/features/financials/bankStatus';
import {
  classifyQboPostFailure,
  resolveFailureActionHref,
} from '@/features/financials/qboPostFailure';
import type { ExpenseStatus, QbPostStatus, Tables } from '@/types/database';

type TxRow = TransactionListRow;

// Sentinel used by the QB Category dropdown to mean "no QB account assigned".
// Radix Select can't carry an empty string as a value, so we round-trip
// through this constant and translate it to `null` at the DB boundary.
const QB_NONE = '__none__';

// Prefix used by SelectItems rendered inside the "Suggested" group. Radix
// requires every SelectItem value in a single Select to be unique; the
// prefix lets a suggestion and its canonical entry coexist.
const QB_SUGGEST_PREFIX = 'suggest:';

// Compound value for the QBO Name picker. Vendors and customers share an
// `id` namespace per-table but collide across — and Radix needs a single
// string per SelectItem value — so we tag the type into the value and
// parse it back on change.
const ENTITY_VENDOR_PREFIX = 'vendor:';
const ENTITY_CUSTOMER_PREFIX = 'customer:';

function encodeEntityValue(
  entity: Pick<QbEntityPick, 'type' | 'id'> | null,
): string {
  if (!entity) return QB_NONE;
  return entity.type === 'vendor'
    ? `${ENTITY_VENDOR_PREFIX}${entity.id}`
    : `${ENTITY_CUSTOMER_PREFIX}${entity.id}`;
}

function decodeEntityValue(
  value: string,
): { type: 'vendor' | 'customer'; id: string } | null {
  if (value === QB_NONE) return null;
  if (value.startsWith(ENTITY_VENDOR_PREFIX)) {
    return { type: 'vendor', id: value.slice(ENTITY_VENDOR_PREFIX.length) };
  }
  if (value.startsWith(ENTITY_CUSTOMER_PREFIX)) {
    return { type: 'customer', id: value.slice(ENTITY_CUSTOMER_PREFIX.length) };
  }
  return null;
}

/**
 * Live bank-feed transactions tab. Owns the post-cutover (>= 2026-05-01)
 * Mercury / Plaid rows: editable QB category, expense approval workflow,
 * Post to QBO button. This is the daily-driver surface that used to be
 * the entire TransactionsPage; the History tab sits beside it.
 */
export function LiveTransactionsTab() {
  const navigate = useNavigate();

  const { data: coa } = useChartOfAccounts();
  const { data: qbEntities } = useQbEntities();
  const updateQbAccount = useUpdateTransactionQbAccount();
  const updateEntity = useUpdateTransactionEntity();
  const postTransactions = usePostTransactionsToQbo();

  // Post-batch focus: when the bulk runner finishes with failures, the
  // summary toast offers a "Show failures" action. Clicking it stuffs the
  // failed ids in here, and we filter the grid client-side to only those
  // rows so the operator can fix-and-retry without scrolling past 100
  // green Posted pills. `null` = no filter (the common case).
  const [failureFilterIds, setFailureFilterIds] = useState<string[] | null>(
    null,
  );

  // P&L drill-down: when the operator clicks a cell on the Actuals P&L
  // grid we get back a URL with `accounts`, `from`, `to`, `accountLabel`,
  // and `monthLabel` search params. Same pre-filter pattern as
  // `failureFilterIds` above — applied as an extra layer on top of
  // `txRows` before the DataTable's own filter / sort / paginate runs,
  // and surfaced as a banner the operator can dismiss with one click.
  const drilldown = useTxDrilldownFilter();

  const bulkPost = useBulkPostToQbo({
    onShowFailures: useCallback(
      (ids: string[]) => setFailureFilterIds(ids.length > 0 ? ids : null),
      [],
    ),
  });

  // "Show both sides of paired transfers" toggle. Default OFF: each
  // internal transfer (Mercury Checking debit + Mercury Credit credit
  // for a CC payoff) collapses to a single row — the lead, by
  // convention. Flipping ON expands every pair so the grid mirrors the
  // raw transactions table for audit / per-account reconciliation.
  // The setting is local-only; it doesn't affect Dashboard MetricCards
  // or the cash-flow chart, which always exclude paired rows in SQL
  // (see migration 72's v_daily_cash_flow rewrite).
  const [showBothTransferSides, setShowBothTransferSides] = useState(false);

  // We now do filtering/searching client-side via TanStack Table, so we
  // fetch a fat-but-bounded page (1k) of transactions and let the grid
  // page through them. If the org ever exceeds this, swap to a
  // server-side row model and pass `manualPagination`.
  const { data, isLoading } = useTransactions({
    limit: 1000,
    collapseTransfers: !showBothTransferSides,
  });

  // Memo the row array so downstream useMemo deps (column IDs, faceted
  // option lists) don't bust their cache on every render just because
  // `data ?? []` produced a fresh array reference.
  const txRows = useMemo<TxRow[]>(() => data ?? [], [data]);

  // Pull category suggestions for every loaded transaction in one shot.
  // We key off the full loaded set rather than the failure-filtered view
  // so toggling the filter doesn't re-issue the RPC.
  const transactionIds = useMemo(() => txRows.map((t) => t.id), [txRows]);
  const { data: suggestionsByTx } = useSuggestedQbCategoriesBatch(transactionIds);
  // Manual category splits (migration 74). Same pattern as suggestions:
  // one batched query that returns a Map<txId, summary> so the QB
  // Category column can render "(N splits)" without one query per row.
  // The summary carries the count + the first split's category name so
  // the column can show "Office Supplies +2 more" at a glance.
  const { data: splitsSummaryByTx } = useTransactionSplitsSummary(transactionIds);

  // The DataTable owns its own column-filter state internally and exposes
  // no controlled API for callers, so we apply both pre-filters (the
  // "Show failures" focus view and the P&L drill-down) on the row array
  // here. Keeps the surgery to a single page-level state instead of
  // reaching into TanStack Table's internals.
  //
  // Order matters when both are active: failures first, then drill-down,
  // so the "Show failures" count in its banner always matches the
  // original ids the bulk runner returned (it's a fixed id list, not a
  // category query), while the drill-down's count reflects "of those
  // failures, how many fall inside this account × month".
  const visibleRows = useMemo<TxRow[]>(() => {
    let out = txRows;
    if (failureFilterIds && failureFilterIds.length > 0) {
      const wanted = new Set(failureFilterIds);
      out = out.filter((t) => wanted.has(t.id));
    }
    if (drilldown.hasFilter) {
      out = out.filter((t) => {
        if (drilldown.accountIds.size > 0) {
          if (!t.qb_account_id || !drilldown.accountIds.has(t.qb_account_id)) {
            return false;
          }
        }
        return isWithinDrilldownRange(t.posted_at, drilldown.from, drilldown.to);
      });
    }
    return out;
  }, [txRows, failureFilterIds, drilldown]);

  // Quick lookup: qb_account_id -> CoA row, so we can render suggestion
  // labels without re-walking the (potentially long) `coa` array per row.
  const coaById = useMemo(() => {
    const m = new Map<string, Tables<'chart_of_accounts'>>();
    for (const a of coa ?? []) m.set(a.id, a);
    return m;
  }, [coa]);

  // Group active QB accounts by account_type for the inline picker. The
  // helper flattens each group into a parent-first, depth-tagged list so
  // the dropdown can render sub-accounts indented under their parent and
  // mark the parents themselves as "locked" (disabled, lighter grey) —
  // QBO won't accept postings against an account that has sub-accounts,
  // so they're shown as headers rather than picks.
  const qbAccountsByType = useMemo(() => buildCoaHierarchyByType(coa), [coa]);
  const lockedAccountIds = useMemo(() => findLockedAccountIds(coa), [coa]);

  const onChangeQbAccount = async (txId: string, nextValue: string) => {
    const stripped = nextValue.startsWith(QB_SUGGEST_PREFIX)
      ? nextValue.slice(QB_SUGGEST_PREFIX.length)
      : nextValue;
    const qb_account_id = stripped === QB_NONE ? null : stripped;
    try {
      await updateQbAccount.mutateAsync({ id: txId, qb_account_id });
      toast.success(qb_account_id ? 'QB category updated' : 'QB category cleared');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Update failed';
      toast.error(msg);
    }
  };

  const onChangeEntity = async (txId: string, nextValue: string) => {
    const entity = decodeEntityValue(nextValue);
    try {
      await updateEntity.mutateAsync({ id: txId, entity });
      toast.success(entity ? 'Name updated' : 'Name cleared');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Update failed';
      toast.error(msg);
    }
  };

  const onPostOne = async (txId: string, isUpdate: boolean) => {
    try {
      const res = await postTransactions.mutateAsync({ transaction_ids: [txId] });
      const result = res.results.find((r) => r.id === txId);
      if (result?.status === 'failed') {
        toast.error(`${isUpdate ? 'Update' : 'Post'} failed: ${result.error ?? 'unknown error'}`);
      } else if (result?.recreated) {
        toast.success(
          'The original entry was gone in QuickBooks — created a fresh one in its place.',
          { duration: 6000 },
        );
      } else {
        toast.success(isUpdate ? 'Updated in QuickBooks.' : 'Posted to QuickBooks.');
      }
      // Receipt is mirrored to QBO as a separate, non-fatal step. Surface
      // the outcome so the operator can see whether the receipt followed
      // the row across. Mirrors the per-row handling in TransactionDetailPage.
      if (result?.attachment === 'failed') {
        toast.warning(
          `Posted, but couldn't attach the receipt in QuickBooks: ${
            result.attachment_error ?? 'unknown error'
          }. The post will retry the upload next time.`,
          { duration: 8000 },
        );
      } else if (result?.attachment === 'uploaded') {
        toast.success('Receipt attached in QuickBooks.');
      } else if (result?.attachment === 'replaced') {
        toast.success('Receipt updated in QuickBooks.');
      } else if (result?.attachment === 'removed') {
        toast.success('Receipt removed from QuickBooks.');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : `${isUpdate ? 'Update' : 'Post'} failed`;
      toast.error(msg);
    }
  };

  // Strict approval queue: rows the cardholder explicitly handed off
  // (expense_status='submitted'), as opposed to the broader
  // qb_post_status='awaiting_approval' bucket which also includes drafts
  // and rejected rows. This is the actionable count the super_admin
  // used to see on the Expense Tracking page before that surface was
  // re-scoped to cardholders.
  const submittedForReviewCount = txRows.filter(
    (t) => t.expense_status === 'submitted',
  ).length;

  // -----------------------------------------------------------------------
  // Column definitions. Each column carries its sort key, optional faceted
  // filter accessor, and a renderer. `arrIncludesSome` is the filterFn used
  // by every faceted dropdown — see DataTable for the helper.
  // -----------------------------------------------------------------------
  const columns = useMemo<ColumnDef<TxRow>[]>(
    () => [
      {
        id: 'posted_at',
        accessorKey: 'posted_at',
        header: 'Date',
        sortingFn: 'datetime',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDate(row.original.posted_at)}
          </span>
        ),
      },
      {
        id: 'description',
        // Operator override beats the bank-feed value, both for display
        // and for the global filter — the override is what the operator
        // expects QuickBooks to show, and the search box should match it.
        // Mirrors effectiveDescriptionFor() in qbo-post-transaction.
        accessorFn: (row) => {
          const ov = row.description_override?.trim();
          return ov && ov.length > 0 ? ov : row.description;
        },
        header: 'Description',
        cell: ({ row, getValue }) => {
          const display = getValue<string>();
          const edited =
            row.original.description_override !== null &&
            (row.original.description_override?.trim().length ?? 0) > 0;
          return (
            <div className="flex flex-col">
              <span className="font-medium">{display}</span>
              {edited && (
                <span className="truncate text-[11px] text-muted-foreground/80">
                  from feed: {row.original.description}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'name',
        // The cell's primary value is the picked vendor / customer name
        // (QBO's "Name" on the Purchase). We fall back to the bank-feed
        // counterparty so the global filter and the inline display still
        // show something useful for un-mapped rows.
        accessorFn: (row) =>
          row.qb_vendor?.name ?? row.qb_customer?.name ?? row.counterparty ?? '',
        header: 'Name',
        cell: ({ row }) => {
          const t = row.original;
          const currentValue = t.qb_vendor_id
            ? encodeEntityValue({ type: 'vendor', id: t.qb_vendor_id })
            : t.qb_customer_id
              ? encodeEntityValue({ type: 'customer', id: t.qb_customer_id })
              : QB_NONE;
          const detectedHint = t.counterparty ?? null;
          const vendors = qbEntities?.vendors ?? [];
          const customers = qbEntities?.customers ?? [];
          const hasEntities = vendors.length > 0 || customers.length > 0;
          return (
            <div onClick={(e) => e.stopPropagation()} className="space-y-1">
              <Select
                value={currentValue}
                onValueChange={(v) => onChangeEntity(t.id, v)}
                disabled={
                  updateEntity.isPending ||
                  !hasEntities ||
                  t.qb_post_status === 'posting'
                }
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue
                    placeholder={
                      !hasEntities
                        ? 'Sync QuickBooks…'
                        : (detectedHint ?? 'Unassigned')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={QB_NONE}>
                    <span className="text-muted-foreground">Unassigned</span>
                  </SelectItem>
                  {vendors.length > 0 && (
                    <SelectGroup>
                      <SelectSeparator />
                      <SelectLabel>Vendors</SelectLabel>
                      {vendors.map((v) => (
                        <SelectItem
                          key={`v-${v.id}`}
                          value={encodeEntityValue(v)}
                        >
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {customers.length > 0 && (
                    <SelectGroup>
                      <SelectSeparator />
                      <SelectLabel>Customers</SelectLabel>
                      {customers.map((c) => (
                        <SelectItem
                          key={`c-${c.id}`}
                          value={encodeEntityValue(c)}
                        >
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              {detectedHint &&
                !t.qb_vendor_id &&
                !t.qb_customer_id && (
                  <p className="truncate text-[11px] text-muted-foreground/80">
                    from feed: {detectedHint}
                  </p>
                )}
            </div>
          );
        },
        size: 240,
      },
      {
        id: 'source',
        // Card swipes show "Karthik •7592"; ACH/wires fall back to the
        // bank account name. For paired internal transfers we append the
        // partner side's bank account so the row reads "Mercury Checking
        // → Mercury Credit" in one glance — that's the whole reason the
        // collapse is safe (the operator can still see what flowed where
        // without expanding the pair). The accessor returns the
        // human-readable string so the global filter (description ∪
        // counterparty ∪ source ∪ qb-category) can match a card's last 4
        // or a partner account name.
        accessorFn: (row) => {
          const ownSide = row.cards
            ? `${row.cards.name_on_card ?? 'Card'} •${row.cards.last_four ?? '????'}`
            : (row.bank_accounts?.name ?? '—');
          if (row.transfer_partner) {
            const otherName = row.transfer_partner.bank_accounts?.name ?? '—';
            // Render the source-as-from / dest-as-to in natural reading
            // order based on direction. The lead is direction='debit'
            // (money out of THIS bank), so for the lead we point AT the
            // partner; for the rare follower-only display, we point FROM
            // the partner.
            return row.direction === 'debit'
              ? `${ownSide} → ${otherName}`
              : `${otherName} → ${ownSide}`;
          }
          return ownSide;
        },
        header: 'Source',
        filterFn: arrIncludesSome,
        cell: ({ row, getValue }) => {
          const t = row.original;
          const display = getValue<string>();
          if (t.transfer_partner) {
            return (
              <div className="flex flex-col gap-0.5">
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <ArrowRightLeft className="h-3 w-3 text-atmosphere-dark" />
                  <span>{display}</span>
                </span>
                <span className="text-[11px] text-muted-foreground/80">
                  Internal transfer
                </span>
              </div>
            );
          }
          return <span className="text-muted-foreground">{display}</span>;
        },
      },
      {
        id: 'qb_category',
        accessorFn: (row) => row.chart_of_accounts?.name ?? '',
        header: 'QB Category',
        // Render an inline Select so the operator can change category
        // without leaving the row. Stop propagation on the cell so
        // clicking the dropdown doesn't navigate to the detail page.
        // Special-case: when the row has manual splits (migration 74)
        // we replace the picker with a chip + "Edit splits" link to
        // the detail page — splits override single-category at post
        // time, and editing them inline doesn't fit a single grid cell.
        cell: ({ row }) => {
          const t = row.original;
          const splitInfo = splitsSummaryByTx?.get(t.id);
          if (splitInfo && splitInfo.count > 0) {
            return (
              <div onClick={(e) => e.stopPropagation()}>
                <Link
                  to={`/financials/transactions/${t.id}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-atmosphere-dark/30 bg-atmosphere-dark/5 px-2.5 py-1.5 text-xs hover:bg-atmosphere-dark/10"
                  title={
                    splitInfo.firstCategoryName
                      ? `${splitInfo.firstCategoryName}${splitInfo.count > 1 ? ` and ${splitInfo.count - 1} more` : ''}`
                      : `${splitInfo.count} splits`
                  }
                >
                  <span className="inline-flex min-w-0 items-center gap-1.5 text-atmosphere-dark">
                    <Split className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate font-medium">
                      {splitInfo.count}-way split
                    </span>
                  </span>
                  {splitInfo.firstCategoryName && (
                    <span className="truncate text-muted-foreground">
                      {splitInfo.firstCategoryName}
                      {splitInfo.count > 1 ? ` +${splitInfo.count - 1}` : ''}
                    </span>
                  )}
                </Link>
              </div>
            );
          }
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Select
                value={t.qb_account_id ?? QB_NONE}
                onValueChange={(v) => onChangeQbAccount(t.id, v)}
                disabled={
                  updateQbAccount.isPending ||
                  (coa ?? []).length === 0 ||
                  t.qb_post_status === 'posting'
                }
              >
                <SelectTrigger className="h-9 w-full">
                  <SelectValue
                    placeholder={
                      (coa ?? []).length === 0 ? 'Sync QuickBooks…' : 'Unassigned'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={QB_NONE}>
                    <span className="text-muted-foreground">Unassigned</span>
                  </SelectItem>
                  {(() => {
                    // Locked parent accounts can sneak into the suggestion
                    // list if a previously-leaf account was demoted into a
                    // sub-account in QBO after the historical post —
                    // filter them out so we never suggest something the
                    // user can't actually pick.
                    const ids = suggestionsByTx?.get(t.id) ?? [];
                    const items = ids
                      .filter((id) => id !== t.qb_account_id && !lockedAccountIds.has(id))
                      .map((id) => coaById.get(id))
                      .filter(
                        (a): a is Tables<'chart_of_accounts'> => !!a && a.is_active,
                      );
                    if (items.length === 0) return null;
                    return (
                      <SelectGroup>
                        <SelectSeparator />
                        <SelectLabel className="flex items-center gap-1.5">
                          <Sparkles className="h-3 w-3 text-atmosphere-dark" />
                          Suggested
                        </SelectLabel>
                        {items.map((a) => (
                          <SelectItem
                            key={`suggest-${a.id}`}
                            value={`${QB_SUGGEST_PREFIX}${a.id}`}
                            className="py-2 leading-6"
                          >
                            {formatQbAccountLabel(a)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })()}
                  {QB_GROUP_ORDER.map(({ key, label }) => {
                    const items = qbAccountsByType.get(key) ?? [];
                    if (items.length === 0) return null;
                    return (
                      <SelectGroup key={key}>
                        <SelectSeparator />
                        <SelectLabel>{label}</SelectLabel>
                        {items.map(({ account, depth, locked }) => (
                          <SelectItem
                            key={account.id}
                            value={account.id}
                            disabled={locked}
                            className={
                              locked
                                ? 'py-2 pl-8 leading-6 text-muted-foreground/60 data-[disabled]:opacity-100'
                                : 'py-2 pl-8 leading-6'
                            }
                            style={{ paddingLeft: `${2 + depth * 1.25}rem` }}
                          >
                            {formatQbAccountLabel(account)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          );
        },
        size: 260,
      },
      {
        id: 'amount_cents',
        // Sort numerically by signed amount: credits push positive, debits
        // negative. The display still renders with explicit ± sign and
        // brand colors.
        accessorFn: (row) =>
          (row.direction === 'credit' ? 1 : -1) * row.amount_cents,
        header: () => <span className="block text-right">Amount</span>,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const t = row.original;
          return (
            <span
              className={
                'block text-right tabular-nums ' +
                (t.direction === 'credit'
                  ? 'text-bright-moss-dark'
                  : 'text-foreground')
              }
            >
              {t.direction === 'credit' ? '+' : '−'}
              {formatCurrency(t.amount_cents)}
            </span>
          );
        },
      },
      {
        id: 'expense_status',
        accessorKey: 'expense_status',
        header: 'Expense Status',
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const t = row.original;
          // Editor renders inline for super_admin (this page is super_admin
          // only) and falls back to the badge for everyone else as a
          // belt-and-suspenders against role drift.
          return (
            <ExpenseStatusEditor
              transactionId={t.id}
              status={t.expense_status}
              qbPostStatus={t.qb_post_status}
            />
          );
        },
        size: 160,
      },
      {
        id: 'qb_post_status',
        accessorKey: 'qb_post_status',
        header: 'QBO Status',
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const t = row.original;
          // Posted rows expose an "Update" affordance: clicking it
          // overwrites the booked QBO Purchase with the current Atlas
          // values (description / memo / category / payee). The edge
          // function picks create vs update off `qb_external_id`, so
          // we just call the same RPC.
          //
          // Bank-failed rows are forced to qb_post_status='skipped' by
          // the trigger (migration 70) and re-rendered as a "Bank
          // failed" warning pill below, so the Post / Update buttons
          // never show. We still pass bank_status through so PostStatusCell
          // can swap rendering.
          const bankFailed = isBankFailStatus(t.bank_status);
          // expense_status='in_qb' means the row is already booked in QBO
          // outside Atlas — the trigger has already pinned qb_post_status
          // to 'skipped' for these, but we explicitly disable Post/Update
          // here too so a stale local cache (e.g. an in-flight invalidate)
          // can never let the operator double-post.
          const alreadyInQb = t.expense_status === 'in_qb';
          // Splits provide their own per-line categorisation, so a
          // splits-only row (qb_account_id=null + splits.length>0)
          // counts as categorised for the purpose of Post / Update.
          const splitInfo = splitsSummaryByTx?.get(t.id);
          const hasCategorisation = !!t.qb_account_id || (splitInfo?.count ?? 0) > 0;
          // While a bulk run is in flight we lock down the per-row Post /
          // Update buttons for every row in the grid (not just the ones in
          // the batch). The Edge Function is idempotent on qb_external_id,
          // so a second click can't double-post — but it CAN confuse the
          // operator's mental model ("did my click do anything?") if it
          // races with the bulk runner's optimistic patches. Easier to
          // freeze the buttons and let the batch drain first.
          const canPost =
            hasCategorisation &&
            !bankFailed &&
            !alreadyInQb &&
            !bulkPost.isRunning &&
            (t.qb_post_status === 'pending' || t.qb_post_status === 'failed');
          const canUpdate =
            hasCategorisation &&
            !bankFailed &&
            !alreadyInQb &&
            !bulkPost.isRunning &&
            t.qb_post_status === 'posted';
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <PostStatusCell
                transactionId={t.id}
                status={t.qb_post_status}
                bankStatus={t.bank_status}
                expenseStatus={t.expense_status}
                lastError={t.qb_last_error}
                canPost={canPost}
                canUpdate={canUpdate}
                onPost={() => onPostOne(t.id, false)}
                onUpdate={() => onPostOne(t.id, true)}
                posting={postTransactions.isPending || bulkPost.isRunning}
                bulkRowState={bulkPost.getRowState(t.id)}
              />
            </div>
          );
        },
      },
      {
        id: '__open',
        enableSorting: false,
        enableHiding: false,
        header: () => null,
        cell: ({ row }) => (
          <Link
            to={`/financials/transactions/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            aria-label="Open transaction"
            className="text-muted-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </Link>
        ),
        size: 32,
      },
    ],
    // We intentionally exclude the inline mutation hooks from the dep
    // array so the columns aren't rebuilt on every keystroke in the
    // global filter. The handlers read from refs implicitly via
    // closure over the latest hook return values, which React Query
    // keeps stable across renders that don't change the cached data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      coa,
      coaById,
      qbAccountsByType,
      lockedAccountIds,
      suggestionsByTx,
      // Splits map drives the QB Category cell's split-aware branch
      // and the Post / Update enable rules. Identity changes when
      // useTransactionSplitsSummary refetches after a save.
      splitsSummaryByTx,
      qbEntities,
      postTransactions.isPending,
      updateQbAccount.isPending,
      updateEntity.isPending,
      // The QBO Status cell overlays the bulk runner's per-row state on
      // top of the cache value, so the column needs to rebuild as workers
      // flip rows queued → posting → posted. `rowStates` is the snapshot
      // map the hook returns; identity changes per worker tick.
      bulkPost.isRunning,
      bulkPost.rowStates,
    ],
  );

  // Faceted filter config. The "Source" facet auto-derives its option
  // list from the column's unique values (every distinct "Karthik
  // •7592" or "Mercury Checking" in the visible data). The "QBO Status"
  // facet uses an explicit option list so labels stay consistent and
  // the order is stable regardless of which statuses are present.
  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'source', title: 'Source' },
      {
        columnId: 'expense_status',
        title: 'Expense Status',
        options: [
          { value: 'draft', label: 'Draft' },
          { value: 'submitted', label: 'Submitted' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'in_qb', label: 'Already in QB' },
        ] satisfies Array<{ value: ExpenseStatus; label: string }>,
      },
      {
        columnId: 'qb_post_status',
        title: 'QBO Status',
        options: [
          { value: 'unassigned', label: 'Unassigned' },
          { value: 'awaiting_approval', label: 'Awaiting approval' },
          { value: 'pending', label: 'Ready to post' },
          { value: 'posting', label: 'Posting' },
          { value: 'posted', label: 'Posted' },
          { value: 'failed', label: 'Failed' },
          { value: 'skipped', label: 'Skipped' },
        ],
      },
    ],
    [],
  );

  // Quick stats for the header chips.
  const counts = useMemo(() => {
    const byStatus = new Map<QbPostStatus, number>();
    for (const t of txRows) {
      byStatus.set(t.qb_post_status, (byStatus.get(t.qb_post_status) ?? 0) + 1);
    }
    return byStatus;
  }, [txRows]);

  // Pre-compute the ids the operator would post from the header "Post N
  // pending" button. Mirrors the per-row `canPost` rules so we never
  // queue a row the server would refuse — bank-failed, in_qb, missing
  // category, transfer follower (which already lands at
  // expense_status='in_qb' via migration 72) — and gives us a concrete
  // count to render on the button label. A splits-only row (no parent
  // qb_account_id but at least one transaction_splits row) counts as
  // categorised here, mirroring the per-row Post button.
  const pendingPostableIds = useMemo<string[]>(() => {
    return txRows
      .filter(
        (t) =>
          (!!t.qb_account_id || (splitsSummaryByTx?.get(t.id)?.count ?? 0) > 0) &&
          !isBankFailStatus(t.bank_status) &&
          t.expense_status !== 'in_qb' &&
          t.qb_post_status === 'pending',
      )
      .map((t) => t.id);
  }, [txRows, splitsSummaryByTx]);

  const onClearFailureFilter = useCallback(() => {
    setFailureFilterIds(null);
    bulkPost.reset();
  }, [bulkPost]);

  return (
    <Card>
      <CardContent className="pt-6">
        {drilldown.hasFilter && (
          // Drill-down banner. Surfaces above the toolbar (and above the
          // failures banner, when both are active) so the operator sees
          // "you're looking at one cell from the P&L" before anything
          // else. Uses the atmosphere palette (the same tint as the
          // transfer-row hover) to distinguish it from the warning-tinted
          // failures banner below — both can be on screen at once but
          // they communicate different things.
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-atmosphere-dark/30 bg-atmosphere-dark/5 px-3 py-2 text-sm">
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 text-atmosphere-dark">
              <Filter className="h-3.5 w-3.5" />
              Drilled in from {drilldown.source ?? 'P&L'}
              {drilldown.accountLabel && (
                <span className="font-medium">
                  {drilldown.accountLabel}
                </span>
              )}
              {drilldown.monthLabel && (
                <span className="text-atmosphere-dark/80">
                  · {drilldown.monthLabel}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                ({visibleRows.length} transaction
                {visibleRows.length === 1 ? '' : 's'})
              </span>
            </span>
            <span className="inline-flex items-center gap-1">
              {drilldown.back && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(drilldown.back!)}
                  className="h-7 gap-1 px-2 text-xs"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to {drilldown.source ?? 'summary'}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={drilldown.clear}
                className="h-7 gap-1 px-2 text-xs"
              >
                <X className="h-3.5 w-3.5" />
                Show all
              </Button>
            </span>
          </div>
        )}
        {failureFilterIds && failureFilterIds.length > 0 && (
          // Post-batch focus banner. Surfaces above the toolbar so it can't
          // be confused with the table's own faceted filter chips, and
          // uses the warning-tint (fresh-salmon-dark) palette to match the
          // failed-row pill in the QBO Status column — eye lands on the
          // banner first, then on the rows it scoped to.
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-fresh-salmon-dark/30 bg-fresh-salmon-dark/5 px-3 py-2 text-sm">
            <span className="inline-flex items-center gap-2 text-fresh-salmon-dark">
              <Filter className="h-3.5 w-3.5" />
              Showing the {failureFilterIds.length} transaction
              {failureFilterIds.length === 1 ? '' : 's'} that failed in the
              last bulk post.
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClearFailureFilter}
              className="h-7 gap-1 px-2 text-xs"
            >
              <X className="h-3.5 w-3.5" />
              Show all
            </Button>
          </div>
        )}
        <DataTable<TxRow, unknown>
          data={visibleRows}
          columns={columns}
          isLoading={isLoading}
          enableRowSelection
          getRowId={(r) => r.id}
          searchPlaceholder="Search description, name, card last-4, or category…"
          facetedFilters={facetedFilters}
          initialPageSize={25}
          pageSizes={[10, 25, 50, 100, 200]}
          tableId="live-transactions"
          enableColumnResizing
          enableTextWrapToggle
          onRowClick={(t) => navigate(`/financials/transactions/${t.id}`)}
          // Mobile card layout — the description is the line the
          // operator scans for ("Stripe", "Verizon"), the source
          // (card last-4 / bank name) lives in the subtitle so the
          // operator can confirm which card swiped, and the signed
          // amount sits prominently on the right with brand-correct
          // green/black for credits/debits. Status chips (QBO post
          // status, expense status) live in the badge row. The
          // detail page is one tap away via the chevron OR the
          // whole-card tap target.
          mobileCard={{
            title: (t) => {
              const ov = t.description_override?.trim();
              return ov && ov.length > 0 ? ov : t.description;
            },
            subtitle: (t) => {
              const ownSide = t.cards
                ? `${t.cards.name_on_card ?? 'Card'} •${t.cards.last_four ?? '????'}`
                : (t.bank_accounts?.name ?? '—');
              // For paired transfers, render the source line as
              // "Mercury Checking → Mercury Credit" so the mobile card
              // mirrors the desktop Source column without needing the
              // separate transfer badge to carry that information.
              const source = t.transfer_partner
                ? t.direction === 'debit'
                  ? `${ownSide} → ${t.transfer_partner.bank_accounts?.name ?? '—'}`
                  : `${t.transfer_partner.bank_accounts?.name ?? '—'} → ${ownSide}`
                : ownSide;
              const name =
                t.qb_vendor?.name ?? t.qb_customer?.name ?? t.counterparty;
              const parts: string[] = [formatDate(t.posted_at)];
              if (name && !t.transfer_partner) parts.push(name);
              parts.push(source);
              return parts.join(' · ');
            },
            badges: (t) => {
              const out: React.ReactNode[] = [];
              // Transfer badge first — the operator's eye should land on
              // "this row is one half of a transfer" before status / category,
              // because that context changes how everything else reads.
              if (t.transfer_partner) {
                out.push(
                  <Badge key="transfer" variant="info" className="gap-1">
                    <ArrowRightLeft className="h-3 w-3" />
                    Transfer
                  </Badge>,
                );
              }
              // Bank-side failure is the most important thing to flag —
              // the operator should never wonder why a row isn't postable.
              const bankLabel = bankFailLabel(t.bank_status);
              if (bankLabel) {
                out.push(
                  <Badge key="bank" variant="warning">
                    Bank: {bankLabel}
                  </Badge>,
                );
              } else if (t.expense_status === 'in_qb') {
                // "Already in QB" wins over the QBO lifecycle badge because
                // that's the operator's explicit "do not post" signal —
                // surfacing "Skipped" here would be technically true but
                // unhelpfully generic.
                out.push(
                  <Badge key="qbo" variant="muted">
                    Already in QB
                  </Badge>,
                );
              } else if (t.qb_post_status === 'posted') {
                out.push(
                  <Badge key="qbo" variant="success">
                    Posted
                  </Badge>,
                );
              } else if (t.qb_post_status === 'failed') {
                // Mirror the desktop cell's classified headline ("Reconnect
                // QuickBooks", "Map bank account", etc.) so the mobile card
                // tells the operator what to do next, not just that
                // something failed.
                const failure = classifyQboPostFailure(t.qb_last_error);
                out.push(
                  <Badge
                    key="qbo"
                    variant="warning"
                    title={failure.description}
                  >
                    {failure.headline}
                  </Badge>,
                );
              } else if (t.qb_post_status === 'awaiting_approval') {
                out.push(
                  <Badge key="qbo" variant="info">
                    Awaiting approval
                  </Badge>,
                );
              } else if (t.qb_post_status === 'pending') {
                out.push(
                  <Badge key="qbo" variant="outline">
                    Ready to post
                  </Badge>,
                );
              } else if (t.qb_post_status === 'unassigned') {
                out.push(
                  <Badge key="qbo" variant="muted">
                    Pick a category
                  </Badge>,
                );
              }
              if (t.chart_of_accounts?.name) {
                out.push(
                  <Badge key="cat" variant="outline">
                    {t.chart_of_accounts.name}
                  </Badge>,
                );
              }
              // Split chip surfaces the multi-category state on the
              // mobile card so the operator doesn't open an "Office
              // Supplies" row only to find five other lines underneath.
              const splitInfo = splitsSummaryByTx?.get(t.id);
              if (splitInfo && splitInfo.count > 0) {
                out.push(
                  <Badge key="split" variant="info" className="gap-1">
                    <Split className="h-3 w-3" />
                    {splitInfo.count}-way split
                  </Badge>,
                );
              }
              return out;
            },
            amount: (t) => ({
              value: `${t.direction === 'credit' ? '+' : '−'}${formatCurrency(t.amount_cents)}`,
              tone:
                t.direction === 'credit' ? 'positive' : 'neutral',
            }),
            // Dim the mobile card for any "the operator doesn't need to act
            // on this" state: already posted, OR already booked in QB
            // outside Atlas. Same visual signal, two different reasons.
            dimmed: (t) =>
              t.qb_post_status === 'posted' || t.expense_status === 'in_qb',
          }}
          // Row tinting:
          //   bank-failed → faint salmon (the warning brand colour) so the
          //                 operator instantly sees the row will never be
          //                 posted, no matter what they do with the rest
          //                 of the columns.
          //   in_qb       → faint neutral grey so the row looks decommissioned
          //                 and the eye doesn't keep landing on it during a
          //                 "Post all pending" sweep. Same intent as posted,
          //                 different reason (booked outside Atlas).
          //   posted      → faint moss-green matching the success badge so
          //                 the eye glides past finished rows.
          // All three keep click-through enabled so an audit drilldown still
          // works from any state.
          rowClassName={(t) => {
            if (isBankFailStatus(t.bank_status)) {
              return 'bg-fresh-salmon-dark/5 text-muted-foreground hover:bg-fresh-salmon-dark/10';
            }
            if (t.expense_status === 'in_qb') {
              return 'bg-muted/40 text-muted-foreground hover:bg-muted/60';
            }
            if (t.qb_post_status === 'posted') {
              return 'bg-bright-moss-dark/5 text-muted-foreground hover:bg-bright-moss-dark/10';
            }
            // Faint atmosphere tint for paired transfers so the row reads
            // as "this is one logical movement of money across two
            // accounts" without competing visually with the post-status
            // tints above.
            if (t.transfer_partner) {
              return 'bg-atmosphere-dark/5 hover:bg-atmosphere-dark/10';
            }
            return undefined;
          }}
          rightSlot={
            <div className="flex flex-wrap items-center gap-3">
              {/* Reveal the follower side of every paired transfer. Off
                  by default — the collapse is the whole point of the
                  pairing logic. Persisted in component state so it
                  resets on a page reload, which matches the operator's
                  mental model: "I'm doing a one-off audit, not changing
                  the default view." */}
              <div className="flex items-center gap-2">
                <Switch
                  id="show-both-transfer-sides"
                  checked={showBothTransferSides}
                  onCheckedChange={setShowBothTransferSides}
                />
                <Label
                  htmlFor="show-both-transfer-sides"
                  className="cursor-pointer text-xs text-muted-foreground"
                >
                  Show both sides of transfers
                </Label>
              </div>
              <Button
                onClick={() => {
                  // Header button posts every Ready-to-post row in the
                  // loaded set. Routed through the bulk runner so the
                  // operator gets the same persistent progress toast and
                  // per-row status overlay as the multi-select path —
                  // previously this called `qbo-post-transaction` with
                  // an empty body to mean "post everything pending" and
                  // showed a single all-or-nothing toast 30s later.
                  if (pendingPostableIds.length === 0) return;
                  void bulkPost.start(pendingPostableIds);
                }}
                disabled={
                  postTransactions.isPending ||
                  bulkPost.isRunning ||
                  pendingPostableIds.length === 0
                }
                variant={pendingPostableIds.length > 0 ? 'default' : 'outline'}
                size="sm"
                className="whitespace-nowrap"
              >
                <Send
                  className={`h-3.5 w-3.5 ${bulkPost.isRunning ? 'animate-pulse' : ''}`}
                />
                {bulkPost.isRunning
                  ? 'Posting…'
                  : pendingPostableIds.length > 0
                    ? `Post ${pendingPostableIds.length} pending`
                    : 'Nothing to post'}
              </Button>
            </div>
          }
          bulkActions={(table) => {
            const selected = table.getSelectedRowModel().rows.map((r) => r.original);
            // Same `canPost` rules as the per-row cell — bank-failed rows,
            // in_qb rows (which includes transfer followers per migration
            // 72), and unassigned rows are silently dropped from the
            // selection. We surface the count on the button so the
            // operator notices when their selection contained
            // ineligible rows. Splits-only rows (no parent qb_account_id
            // but at least one transaction_splits row) count as
            // categorised here.
            const postable = selected.filter(
              (t) =>
                (!!t.qb_account_id ||
                  (splitsSummaryByTx?.get(t.id)?.count ?? 0) > 0) &&
                !isBankFailStatus(t.bank_status) &&
                t.expense_status !== 'in_qb' &&
                (t.qb_post_status === 'pending' || t.qb_post_status === 'failed'),
            );
            const skipped = selected.length - postable.length;
            return (
              <Button
                size="sm"
                variant="default"
                disabled={
                  postTransactions.isPending ||
                  bulkPost.isRunning ||
                  postable.length === 0
                }
                onClick={() => {
                  // Eligible rows go into the queue; the rest are silently
                  // skipped — the operator sees the count in the button
                  // label and can re-check the selection if it surprised
                  // them. Clearing the selection up front prevents a
                  // second click from re-queuing the same rows.
                  void bulkPost.start(postable.map((t) => t.id));
                  table.resetRowSelection();
                }}
                title={
                  skipped > 0
                    ? `${skipped} selected row${skipped === 1 ? '' : 's'} ineligible (no category, already in QB, or bank-failed)`
                    : undefined
                }
              >
                <Send className="h-3.5 w-3.5" />
                Post {postable.length} to QBO
                {skipped > 0 && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    (+{skipped} skipped)
                  </span>
                )}
              </Button>
            );
          }}
          emptyState={
            drilldown.hasFilter ? (
              // The drill-down banner already explains the scope, so the
              // empty state's job here is to point the operator at the
              // most likely cause: a pre-cutover month sliced against the
              // Live tab (which only owns post-2026-05-01 data). The
              // History tab is one click away on the same page.
              <EmptyState
                icon={ArrowLeftRight}
                title="No live transactions in this slice"
              />
            ) : (
              <EmptyState
                icon={ArrowLeftRight}
                title="No transactions"
              />
            )
          }
        />
        {!isLoading && txRows.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
            {submittedForReviewCount > 0 && (
              // Approval-queue chip — visually distinct (info variant + the
              // same CircleDashed glyph the row badges use) from the
              // outline QBO-status counters so the super_admin's eye lands
              // on the actionable number first.
              <Badge variant="info" className="gap-1">
                <CircleDashed className="h-3 w-3" />
                Awaiting your review: {submittedForReviewCount}
              </Badge>
            )}
            {(['pending', 'awaiting_approval', 'posted', 'failed', 'skipped'] as const).map(
              (s) =>
                (counts.get(s) ?? 0) > 0 && (
                  <Badge key={s} variant="outline">
                    {labelForStatus(s)}: {counts.get(s)}
                  </Badge>
                ),
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function labelForStatus(s: QbPostStatus): string {
  switch (s) {
    case 'unassigned':
      return 'Unassigned';
    case 'awaiting_approval':
      return 'Awaiting approval';
    case 'pending':
      return 'Ready to post';
    case 'posting':
      return 'Posting';
    case 'posted':
      return 'Posted';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
  }
}

function PostStatusCell({
  transactionId,
  status,
  bankStatus,
  expenseStatus,
  lastError,
  canPost,
  canUpdate,
  onPost,
  onUpdate,
  posting,
  bulkRowState,
}: {
  /** Row id — needed so the failure branch can route the operator to the
   *  detail page (the "Open & fix" / "Edit & retry" recoveries). */
  transactionId: string;
  status: QbPostStatus;
  bankStatus: string | null;
  expenseStatus: ExpenseStatus;
  lastError: string | null;
  canPost: boolean;
  canUpdate: boolean;
  onPost: () => void;
  onUpdate: () => void;
  posting: boolean;
  /** Optional overlay from the bulk-post runner. Most lifecycle states
   *  (`posting`, `posted`, `failed`) are already reflected in the cache,
   *  so we only need to intercept the renderer for the bulk-only states:
   *  `queued` (the worker hasn't reached this row yet — the cache still
   *  says 'pending' and would otherwise render a clickable Post button)
   *  and `posted` with `recreated: true` (the cell shows the standard
   *  green Posted badge, but we add a small "Recreated" tag so the
   *  operator notices the original was missing in QBO without having
   *  to open the toast / detail page). */
  bulkRowState?: BulkPostRowState;
}) {
  // Bulk-runner overlay takes priority over every cache-driven branch
  // below — once the operator clicks "Post N to QBO", these pills are the
  // operator's only signal that the runner has seen each row. The cache
  // value still says 'pending' for queued rows until a worker picks them
  // up, so without this branch the cell would render its clickable Post
  // button mid-batch and confuse the operator.
  if (bulkRowState?.status === 'queued') {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
        title="Waiting in the bulk-post queue"
      >
        <CircleDashed className="h-3.5 w-3.5" />
        Queued
      </span>
    );
  }
  // Bank-side failure trumps the QBO lifecycle in the UI: the trigger
  // pinned qb_post_status='skipped' (see migration 70), and we want to
  // surface "this never settled at Mercury" instead of the generic
  // "Skipped" badge so the operator knows why this row is inert.
  const bankLabel = bankFailLabel(bankStatus);
  if (bankLabel) {
    return (
      <Badge
        variant="warning"
        className="gap-1"
        title={`Mercury reported this transaction as ${bankLabel.toLowerCase()} — nothing to post to QuickBooks.`}
      >
        <AlertTriangle className="h-3 w-3" />
        Bank: {bankLabel}
      </Badge>
    );
  }
  // "Already in QuickBooks" — booked in QBO outside Atlas (migration 71).
  // The trigger pins qb_post_status='skipped' for these too, so this
  // branch catches the explicit operator decision before falling through
  // to the generic "Skipped" rendering. Rendered with the same BookCheck
  // glyph as the ExpenseStatusEditor for a consistent visual story
  // across the two columns.
  if (expenseStatus === 'in_qb') {
    return (
      <Badge
        variant="muted"
        className="gap-1"
        title="Already booked in QuickBooks outside Atlas. Change the expense status to re-enable posting."
      >
        <BookCheck className="h-3 w-3" />
        Already in QB
      </Badge>
    );
  }
  if (status === 'posted') {
    const recreated =
      bulkRowState?.status === 'posted' && bulkRowState.recreated === true;
    return (
      <div className="flex items-center gap-2">
        <Badge
          variant="success"
          className="gap-1"
          title={
            recreated
              ? 'The original QuickBooks entry was missing — a fresh one was created in its place.'
              : 'Posted to QuickBooks'
          }
        >
          <ShieldCheck className="h-3 w-3" />
          {recreated ? 'Re-created' : 'Posted'}
        </Badge>
        {canUpdate && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onUpdate}
            disabled={posting}
            className="h-7 px-2 text-xs"
            title="Overwrite the booked Purchase in QuickBooks with the current Atlas values"
          >
            <Send className="h-3 w-3" />
            Update
          </Button>
        )}
      </div>
    );
  }
  if (status === 'posting') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <CircleDashed className="h-3.5 w-3.5 animate-spin" />
        Posting…
      </span>
    );
  }
  if (status === 'failed') {
    // Classify the failure so we can show a "do this next" button instead
    // of a generic Retry — token expiry sends the operator to Integrations,
    // a missing bank mapping sends them to /financials/bank-accounts, a
    // QBO validation error opens the detail page where the field editor
    // and AI suggester live, etc. Falls through to a plain Retry for
    // anything the classifier doesn't recognise.
    const failure = classifyQboPostFailure(lastError);
    const href = resolveFailureActionHref(failure.action, transactionId);
    // Action buttons that navigate are always shown (the operator needs
    // to fix something elsewhere first). The Retry-style button still
    // gates on `canPost` so we don't queue a row the server would refuse
    // (missing category, bank-failed, mid-bulk-run).
    const renderAction = () => {
      if (failure.action.kind === 'none') return null;
      if (failure.action.kind === 'retry') {
        if (!canPost) return null;
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={onPost}
            disabled={posting}
            className="h-7 text-xs"
            title={failure.description}
          >
            {failure.action.label}
          </Button>
        );
      }
      if (!href) return null;
      return (
        <Button
          asChild
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          title={failure.description}
        >
          <Link to={href}>{failure.action.label}</Link>
        </Button>
      );
    };
    return (
      <div className="space-y-1">
        <span
          className="inline-flex items-center gap-1.5 text-xs text-fresh-salmon-dark"
          title={lastError ?? undefined}
        >
          <XCircle className="h-3.5 w-3.5" />
          {failure.headline}
        </span>
        {renderAction()}
      </div>
    );
  }
  if (status === 'skipped') {
    return <Badge variant="muted">Skipped</Badge>;
  }
  if (status === 'awaiting_approval') {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-atmosphere-dark"
        title="Awaiting approval before it can post to QuickBooks"
      >
        <CircleDashed className="h-3.5 w-3.5" />
        Awaiting approval
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={onPost}
        disabled={!canPost || posting}
        className="h-7 text-xs"
      >
        <Send className="h-3 w-3" />
        Post
      </Button>
    );
  }
  return <span className="text-xs text-muted-foreground">Pick a category</span>;
}
